import { localize } from "client_legacy/ndaw/culture";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { DocumentItemViewModel, ListLocationViewModel } from "client/Api/Documents/SharePoint/common";
import { CrossStoreDestination } from "client/Api/Documents/CrossStoreOperations/crossStoreActions";
import {
	ItemPickerLocation,
	SharePointPickerLocation,
	WebDavPickerLocation
} from "client/Features/Documents/ItemPicker/ItemPicker";
import { FileSystemItemModel } from "client/Api/Documents/WebDav/webDav";
import { DocumentStoreType } from "client/Api/Documents/common";
import { convertToLocationModel } from "../SharePoint/listLocationModelProvider";

interface SharePointThingsToMove {
	storeKind: DocumentStoreType.SharePoint;
	source: SharePointPickerLocation;
	documents: readonly DocumentItemViewModel[];
}

interface WebDavThingsToMove {
	storeKind: DocumentStoreType.WebDav;
	source: WebDavPickerLocation;
	documents: readonly FileSystemItemModel[];
}

type ThingsToMove = SharePointThingsToMove | WebDavThingsToMove;

interface SharePointThingsToCopy {
	storeKind: DocumentStoreType.SharePoint;
	source: SharePointPickerLocation;
	documents: readonly DocumentItemViewModel[];
}

interface WebDavThingsToCopy {
	storeKind: DocumentStoreType.WebDav;
	source: WebDavPickerLocation;
	documents: readonly FileSystemItemModel[];
}

type ThingsToCopy = SharePointThingsToCopy | WebDavThingsToCopy;

export interface WebDavDestination {
	storeKind: DocumentStoreType.WebDav;
	target: {
		serverId: number;
		folderId: string;
	};
	name: string;
}

export interface SharePointDestination {
	storeKind: DocumentStoreType.SharePoint;
	target: {
		viewId: string;
		location: ListLocationViewModel;
		folderId: number | null;
	};
	name: string;
}

type Destination = WebDavDestination | SharePointDestination;

type CopyWithoutNameConfiguration = {
	shouldShow: false;
};

type CopyWithNameConfiguration = {
	shouldShow: true;
	supportsExtension: boolean;
	isFolder: boolean;
	name: string;
};

type CopyNameConfiguration = CopyWithoutNameConfiguration | CopyWithNameConfiguration;

type ClientSideNameValidationResult = { isValid: true; } | { isValid: false; error: string; };

function convertToDestination(selection: ItemPickerLocation): Destination;
function convertToDestination(selection: ItemPickerLocation | null): Destination | null;
function convertToDestination(selection: ItemPickerLocation | null): Destination | null {
	if (!selection) {
		return null;
	}

	switch (selection.kind) {
		case DocumentStoreType.SharePoint:
			return {
				storeKind: DocumentStoreType.SharePoint,
				target: {
					location: selection.folder.location,
					viewId: selection.folder.viewId,
					folderId: selection.folder.id
				},
				name: selection.folder.name
			};
		case DocumentStoreType.WebDav:
			return {
				storeKind: DocumentStoreType.WebDav,
				target: {
					serverId: selection.folder.server.id,
					folderId: selection.folder.id
				},
				name: selection.folder.name
			};
		default:
			throw new UnreachableCaseError(selection);
	}
}

function convertToCrossStoreDestination(destination: Destination): CrossStoreDestination {
	switch (destination.storeKind) {
		case DocumentStoreType.SharePoint:
			return {
				name: destination.name,
				storeType: DocumentStoreType.SharePoint,
				sharepoint: {
					location: convertToLocationModel(
						destination.target
							.location
					),
					folderId: destination.target.folderId
				}
			};
		case DocumentStoreType.WebDav:
			return {
				name: destination.name,
				storeType: DocumentStoreType.WebDav,
				webdav: {
					folderId: destination.target.folderId,
					serverId: destination.target.serverId
				}
			};
		default:
			throw new UnreachableCaseError(destination);
	}
}

const copyNotConfiguredMessage: string = localize("_ItsNotPossibleToCopyItemsToThisLocationDueToConfigurationPleaseContactYourAdminForMoreInformation");
const moveNotConfiguredMessage: string = localize("_ItsNotPossibleToMoveItemsToThisLocationDueToConfigurationPleaseContactYourAdminForMoreInformation");

export {
	convertToDestination,
	copyNotConfiguredMessage,
	moveNotConfiguredMessage,

	convertToCrossStoreDestination,
	type ThingsToMove,
	type WebDavThingsToMove,
	type SharePointThingsToMove,
	type ThingsToCopy,
	type SharePointThingsToCopy,
	type WebDavThingsToCopy,
	type Destination,
	type CopyNameConfiguration,
	type ClientSideNameValidationResult
};
